import {
  ApiError,
  blankMessage,
  cancelEvent,
  cancelScheduledSend,
  discardMessage,
  closeEvent,
  createMessage,
  getEvent,
  getMessage,
  getOriginal,
  listDemoAudiences,
  listSenders,
  listSources,
  listTemplates,
  llmRefine,
  llmStatus,
  llmTranslate,
  previewAudience,
  patchEvent,
  patchMessage,
  previewMessage,
  draftFromMessage,
  saveMessageAsTemplate,
  sendMessage,
  type Template,
  type Event,
  type Message,
  type MessagePhase,
  type Preview,
  type Sender,
  type DemoAudience,
  type AudiencePreview,
  type ImportSource,
  type LegacyOriginal,
} from "@/api";
import { ScheduleDialog } from "@/components/ScheduleDialog";
import { HtmlEditor } from "@/components/editor/HtmlEditor";
import { SubjectField } from "@/components/editor/SubjectField";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DeliveryProgressBar } from "@/components/ui/DeliveryProgressBar";
import { cn } from "@/lib/cn";
import { dateWarnings, isPastStart } from "@/lib/dateCheck";
import { englishValue, englishValues } from "@/lib/enValues";
import { formatLong, fromLocalValue, maintenanceStart } from "@/lib/schedule";
import {
  emptyHtml,
  formatDateTime,
  formatHtmlSource,
  formatRelativeAge,
  htmlToText,
  placeholderNames,
  substitutePlaceholders,
  unresolvedPlaceholderNames,
} from "@/lib/format";
import {
  defaultPhaseForType,
  bounceCategoryLabel,
  EMPTY_PLACEHOLDERS,
  EVENT_TYPE_LABEL,
  eventStatusLabel,
  isOpenEvent,
  MESSAGE_PHASE_HINT,
  MESSAGE_PHASE_LABEL,
  phasesForType,
  MESSAGE_STATE_LABEL,
  PLACEHOLDER_KEYS,
  PLACEHOLDER_LABEL,
  statusTone,
  typeTone,
} from "@/lib/labels";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

type DocMode = "doc" | "mail" | "source" | "text";
type SaveState = "idle" | "saving" | "saved" | "error";

const PREVIEW_HEADERS = ["From", "List-Id", "Thread-Topic", "Auto-Submitted", "Precedence"] as const;

/** Stable identity: the editor relabels its chips when this array changes. */
const PH_FIELDS = PLACEHOLDER_KEYS.map((key) => ({ name: key, label: PLACEHOLDER_LABEL[key] }));

type RefineState = { loading: boolean; error: string | null; undoHtml: string | null };
const EMPTY_REFINE: RefineState = { loading: false, error: null, undoHtml: null };

type TranslateUndo = { subjectEn: string; htmlEn: string; placeholdersEn: Record<string, string> };
type TranslateState = {
  loading: boolean;
  error: string | null;
  warnings: string[];
  confirm: boolean;
  undo: TranslateUndo | null;
};
const EMPTY_TRANSLATE: TranslateState = {
  loading: false,
  error: null,
  warnings: [],
  confirm: false,
  undo: null,
};

const NO_ANSWER =
  "Keine Antwort vom Server. Der Versand läuft vielleicht trotzdem – bitte erst in der Queue nachsehen, bevor du noch einmal sendest.";

/** What a failed send means for the user: was anything sent, what to do next. */
function sendErrorText(err: unknown): string {
  if (!(err instanceof ApiError)) return NO_ANSWER;
  if (err.code === "directory_not_synced") {
    return "Quelle noch nicht synchronisiert. Unter Quellen einmal syncen, solange die Quelldatenbank erreichbar ist.";
  }
  if (err.status === 409 && err.message === "message is already being sent") {
    return "Diese Mail wird schon verschickt – Fortschritt oben und in der Queue. Nicht noch einmal senden.";
  }
  if (err.status === 500) {
    return "Serverfehler – es wurde nichts verschickt. Bitte noch einmal senden.";
  }
  // A 502 with a JSON detail is our own SMTP report; without one the proxy gave up waiting.
  if (err.status >= 502 && err.status <= 504 && err.body === undefined) return NO_ANSWER;
  return err.message;
}

function sameText(a: string, b: string): boolean {
  const norm = (value: string) => htmlToText(value).split(/\s+/).filter(Boolean).join(" ");
  return norm(a) === norm(b);
}

/** Drops blank values so DE values remain visible as the fallback. */
function nonEmptyValues(values: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value.trim() !== ""));
}

function LegacyMailView({
  event,
  messages,
  message,
  saveError,
  onSelect,
  onSaveTemplate,
  onDraft,
}: {
  event: Event;
  messages: Message[];
  message: Message;
  saveError: string | null;
  onSelect: (item: Message) => void;
  onSaveTemplate: () => void;
  onDraft: () => void;
}) {
  const [original, setOriginal] = useState<LegacyOriginal | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setOriginal(null);
    setError(null);
    getOriginal(message.id)
      .then((data) => {
        if (!cancelled) setOriginal(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Original konnte nicht geladen werden.");
      });
    return () => {
      cancelled = true;
    };
  }, [message.id]);

  const rows: [string, string][] = original
    ? [
        ["Betreff", original.subject || "—"],
        ["Gesendet am", original.sent_at ? formatDateTime(original.sent_at) : "—"],
        ["Listen", original.lists.length ? original.lists.join(", ") : "—"],
        ["Absender", original.sender || "—"],
      ]
    : [];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-hairline bg-surface px-4 py-2">
        <span className="max-w-xs truncate text-sm font-medium">{event.title || message.subject_de || "Ohne Titel"}</span>
        <Badge>Archiv</Badge>
        <div className="ml-auto flex items-center gap-1.5">
          <Button size="sm" onClick={onDraft}>
            Als Entwurf
          </Button>
          <Button size="sm" onClick={onSaveTemplate}>
            Als Vorlage
          </Button>
        </div>
      </div>
      {saveError ? <p className="border-b border-hairline px-4 py-1.5 text-xs text-danger">{saveError}</p> : null}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <div className="min-h-0 flex-1 overflow-auto bg-bg p-4">
          <div className="mx-auto w-full max-w-[680px] space-y-3">
            <p className="text-[11px] text-muted">Alt-Mail aus dem Mailman-Archiv, unverändert übernommen.</p>
            {error ? <p className="text-xs text-danger">{error}</p> : null}
            {!original && !error ? <div className="h-24 animate-pulse rounded-lg bg-surface-subtle" /> : null}
            {original ? (
              <>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg border border-hairline bg-surface px-3 py-2 text-xs">
                  {rows.map(([label, value]) => (
                    <div key={label} className="contents">
                      <dt className="text-muted">{label}</dt>
                      <dd className="min-w-0 break-words">{value}</dd>
                    </div>
                  ))}
                </dl>
                <pre className="whitespace-pre-wrap break-words rounded-lg border border-hairline bg-surface p-4 font-mono text-xs">
                  {original.text}
                </pre>
              </>
            ) : null}
          </div>
        </div>
        {messages.length > 1 ? (
          <aside className="flex w-full shrink-0 flex-col gap-4 overflow-auto border-t border-hairline bg-surface p-3 lg:w-64 lg:border-l lg:border-t-0">
            <section>
              <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">
                Mails in diesem Vorgang
              </h2>
              <div className="flex flex-col gap-1">
                {messages.map((item, index) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onSelect(item)}
                    className={cn(
                      "rounded-md border px-2 py-1 text-left text-xs",
                      item.id === message.id
                        ? "border-accent bg-accent-dim text-accent"
                        : "border-hairline text-muted hover:bg-surface-subtle",
                    )}
                  >
                    <span className="block truncate">{item.subject_de || `Mail ${index + 1}`}</span>
                  </button>
                ))}
              </div>
            </section>
          </aside>
        ) : null}
      </div>
    </div>
  );
}

// Keyed by id: "Als Entwurf" jumps from one event to another, and no state of
// the old one (address, start time, acknowledgements) may carry over.
export function EventEditorPage() {
  const { id } = useParams();
  return <EventEditor key={id} />;
}

function EventEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const wantedMessageRef = useRef<string | null>(null);
  wantedMessageRef.current = searchParams.get("message");
  const [event, setEvent] = useState<Event | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [message, setMessage] = useState<Message | null>(null);
  const [senders, setSenders] = useState<Sender[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [placeholders, setPlaceholders] = useState<Record<string, string>>({ ...EMPTY_PLACEHOLDERS });
  const [senderId, setSenderId] = useState("");
  const [phase, setPhase] = useState<MessagePhase>("announcement");
  const [subjectDe, setSubjectDe] = useState("");
  const [subjectEn, setSubjectEn] = useState("");
  const [htmlDe, setHtmlDe] = useState("");
  const [htmlEn, setHtmlEn] = useState("");
  const [placeholdersEn, setPlaceholdersEn] = useState<Record<string, string>>({});

  const [llmAvailable, setLlmAvailable] = useState(false);
  // EN subject/body are filled by the translation; the editors stay folded unless asked for.
  // Without an LLM they are the only way to write English, so they are always shown.
  const [enOpen, setEnOpen] = useState(false);
  const showEn = enOpen || !llmAvailable;
  const [refineDe, setRefineDe] = useState<RefineState>(EMPTY_REFINE);
  const [refineEn, setRefineEn] = useState<RefineState>(EMPTY_REFINE);
  const [translateState, setTranslateState] = useState<TranslateState>(EMPTY_TRANSLATE);

  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [docMode, setDocMode] = useState<DocMode>("doc");
  const [mailFrameHeight, setMailFrameHeight] = useState(900);

  const [address, setAddress] = useState("");
  const [listIdShort, setListIdShort] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [audiences, setAudiences] = useState<DemoAudience[]>([]);
  const [target, setTarget] = useState<"one" | "demo" | "demo-gmail" | "filter">("one");
  const [sources, setSources] = useState<ImportSource[]>([]);
  const [sourceSlug, setSourceSlug] = useState("");
  const [allOccupied, setAllOccupied] = useState(true);
  const [selectedShorts, setSelectedShorts] = useState<string[]>([]);
  const [audiencePreview, setAudiencePreview] = useState<AudiencePreview | null>(null);
  const [audienceError, setAudienceError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<string | null>(null);
  // Shown while a send to many is being taken on; the request can take a moment.
  const [sendNotice, setSendNotice] = useState<string | null>(null);
  const [pendingSend, setPendingSend] = useState<"one" | "demo" | "demo-gmail" | "filter" | null>(null);
  // A maintenance notice without an English version only goes out once this is ticked.
  const [enMissingAck, setEnMissingAck] = useState(false);
  useEffect(() => {
    setEnMissingAck(false);
  }, [pendingSend]);
  const [wohnheimQuery, setWohnheimQuery] = useState("");
  const [testOpen, setTestOpen] = useState(false);
  const [audienceOpen, setAudienceOpen] = useState(false);
  const audienceButtonRef = useRef<HTMLButtonElement>(null);
  const audiencePopoverRef = useRef<HTMLDivElement>(null);
  const [showAllFields, setShowAllFields] = useState(false);
  const [resolving, setResolving] = useState(false);

  const phRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const dirtyRef = useRef(false);
  const skipDirtyRef = useRef(false);
  const persistGen = useRef(0);
  const saveTimer = useRef<number | null>(null);
  const messageIdRef = useRef<string | null>(null);
  const lockedRef = useRef(false);

  const locked = message?.state !== "draft";
  lockedRef.current = locked;
  const legacyRef = useRef(false);
  legacyRef.current = message?.origin === "legacy_import";

  function audienceIdOf(
    item: Message | null,
    lists: DemoAudience[] = audiences,
  ): "demo" | "demo-gmail" | "one" {
    if (!item) return "one";
    if (item.audience === "demo" || item.audience === "demo-gmail") return item.audience;
    const got = new Set((item.deliveries ?? []).map((row) => row.address.toLowerCase()));
    if (!got.size) return "one";
    for (const audience of lists) {
      const want = audience.recipients.map((row) => row.address.toLowerCase());
      if (want.length && want.every((address) => got.has(address))) return audience.id;
    }
    return "one";
  }

  const sentAudience = audiences.find((item) => item.id === audienceIdOf(message));
  const enabledSources = sources.filter((item) => item.enabled);
  const activeSource =
    sources.find((item) => item.slug === sourceSlug) ?? enabledSources[0] ?? null;
  const sourceRules = useMemo(
    () =>
      [...(activeSource?.rules ?? [])].sort((a, b) =>
        (a.title || a.slug).localeCompare(b.title || b.slug, "de"),
      ),
    [activeSource],
  );
  // The filter only narrows the unticked Wohnheime; ticked ones never disappear.
  const visibleRules = sourceRules.filter(
    (item) =>
      selectedShorts.includes(item.slug) ||
      (item.title || item.slug).toLowerCase().includes(wohnheimQuery.trim().toLowerCase()),
  );

  // Close the Empfänger popover on a click outside it (or its button) and on Escape.
  useEffect(() => {
    if (!audienceOpen) return;
    function onDoc(e: MouseEvent) {
      const target = e.target as Node;
      if (audiencePopoverRef.current?.contains(target) || audienceButtonRef.current?.contains(target)) return;
      setAudienceOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setAudienceOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [audienceOpen]);

  const applyEvent = useCallback((next: Event, selectedId?: string) => {
    setEvent(next);
    setTitle(next.title);
    setPlaceholders({ ...EMPTY_PLACEHOLDERS, ...next.placeholders });
    setPlaceholdersEn({ ...(next.placeholders_en ?? {}) });
    setSenderId(next.sender_id ?? "");
    const filt = next.audience_filter;
    const shorts = filt?.rules ?? filt?.shorts ?? [];
    setAllOccupied(!shorts.length);
    setSelectedShorts(shorts);
    if (filt?.source) setSourceSlug(filt.source);
    const list = next.messages ?? [];
    setMessages(list);
    const selected =
      list.find((item) => item.id === selectedId) ??
      list.find((item) => item.state === "draft") ??
      list[0] ??
      null;
    setMessage(selected);
    if (selected) {
      skipDirtyRef.current = true;
      messageIdRef.current = selected.id;
      setPhase(selected.phase);
      setSubjectDe(selected.subject_de);
      setSubjectEn(selected.subject_en);
      setHtmlDe(selected.html_de);
      setHtmlEn(selected.html_en);
    }
  }, []);

  const refreshPreview = useCallback(async (messageId: string) => {
    try {
      const next = await previewMessage(messageId);
      setPreview(next);
      setPreviewError(null);
    } catch (err) {
      setPreviewError(err instanceof ApiError ? err.message : "Vorschau fehlgeschlagen.");
    }
  }, []);

  async function handleRefine(language: "de" | "en") {
    const html = language === "de" ? htmlDe : htmlEn;
    const setState = language === "de" ? setRefineDe : setRefineEn;
    if (locked || emptyHtml(html)) return;
    setState({ loading: true, error: null, undoHtml: null });
    try {
      const result = await llmRefine({ html, language, event_id: event?.id });
      if (language === "de") {
        setHtmlDe(result.html);
      } else {
        setHtmlEn(result.html);
      }
      setState({ loading: false, error: null, undoHtml: html });
    } catch (err) {
      setState({
        loading: false,
        error: err instanceof ApiError ? err.message : "Überarbeiten fehlgeschlagen.",
        undoHtml: null,
      });
    }
  }

  function handleUndoRefine(language: "de" | "en") {
    const state = language === "de" ? refineDe : refineEn;
    if (state.undoHtml === null) return;
    if (language === "de") {
      setHtmlDe(state.undoHtml);
    } else {
      setHtmlEn(state.undoHtml);
    }
    (language === "de" ? setRefineDe : setRefineEn)(EMPTY_REFINE);
  }

  // Message made from a template whose German text was not changed: its English
  // text is already right, so "Übersetzen" only translates the placeholder values.
  const sourceTemplate = message?.created_from.startsWith("template:")
    ? templates.find((item) => `template:${item.id}` === message.created_from)
    : undefined;
  const translateValuesOnly =
    !!sourceTemplate &&
    !emptyHtml(htmlEn) &&
    subjectDe.trim() === sourceTemplate.subject_de.trim() &&
    sameText(htmlDe, sourceTemplate.html_de);

  function handleTranslateClick() {
    if (locked || emptyHtml(htmlDe) || translateState.loading) return;
    if (translateValuesOnly) {
      void runTranslate();
      return;
    }
    const hasEnContent = subjectEn.trim() !== "" || !emptyHtml(htmlEn);
    if (hasEnContent) {
      setTranslateState((prev) => ({ ...prev, confirm: true, error: null }));
      return;
    }
    void runTranslate();
  }

  async function runTranslate() {
    setTranslateState((prev) => ({ ...prev, loading: true, error: null, confirm: false }));
    try {
      const result = await llmTranslate({
        subject_de: subjectDe,
        html_de: htmlDe,
        placeholders,
        event_id: event?.id,
        values_only: translateValuesOnly,
      });
      const undo: TranslateUndo = { subjectEn, htmlEn, placeholdersEn };
      if (result.subject_en !== null) setSubjectEn(result.subject_en);
      if (result.html_en !== null) setHtmlEn(result.html_en);
      setPlaceholdersEn((prev) => ({ ...prev, ...result.placeholders_en }));
      // The result is what the user wants to check next, so it must not stay folded.
      setEnOpen(true);
      setTranslateState({
        loading: false,
        error: null,
        warnings: result.warnings ?? [],
        confirm: false,
        undo,
      });
    } catch (err) {
      setTranslateState((prev) => ({
        ...prev,
        loading: false,
        confirm: false,
        error: err instanceof ApiError ? err.message : "Übersetzen fehlgeschlagen.",
      }));
    }
  }

  function handleUndoTranslate() {
    const undo = translateState.undo;
    if (!undo) return;
    setSubjectEn(undo.subjectEn);
    setHtmlEn(undo.htmlEn);
    setPlaceholdersEn(undo.placeholdersEn);
    setTranslateState((prev) => ({ ...prev, undo: null }));
  }

  function handleHtmlDeChange(html: string) {
    setHtmlDe(html);
    setRefineDe((prev) => (prev.undoHtml === null ? prev : EMPTY_REFINE));
  }

  function handleHtmlEnChange(html: string) {
    setHtmlEn(html);
    setRefineEn((prev) => (prev.undoHtml === null ? prev : EMPTY_REFINE));
  }

  function refineControls(language: "de" | "en") {
    if (!llmAvailable) return null;
    const state = language === "de" ? refineDe : refineEn;
    const html = language === "de" ? htmlDe : htmlEn;
    const disabled = locked || emptyHtml(html) || state.loading;
    return (
      <>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={disabled}
          onClick={() => void handleRefine(language)}
          className="h-6 px-1.5 text-[11px]"
        >
          {state.loading ? "…" : "✦ Überarbeiten"}
        </Button>
        {state.undoHtml !== null ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => handleUndoRefine(language)}
            className="h-6 px-1.5 text-[11px]"
          >
            ↩ Rückgängig
          </Button>
        ) : null}
        {state.error ? <span className="px-1 text-[11px] text-danger">{state.error}</span> : null}
      </>
    );
  }

  const persist = useCallback(async (): Promise<boolean> => {
    if (!id || !messageIdRef.current) return false;
    // Alt-Mails are read-only; nothing to save.
    if (legacyRef.current) return true;
    const gen = persistGen.current;
    setSaveState("saving");
    setSaveError(null);
    try {
      const patchedEvent = await patchEvent(id, {
        title: title.trim(),
        placeholders,
        placeholders_en: placeholdersEn,
        sender_id: senderId || null,
        ...(event?.audience_snapshot_id
          ? {}
          : {
              audience_filter: {
                source: sourceSlug || null,
                rules: allOccupied ? [] : selectedShorts,
              },
            }),
      });
      if (patchedEvent) {
        setEvent((prev) => (prev ? { ...prev, ...patchedEvent, messages: prev.messages } : patchedEvent));
      }
      if (!lockedRef.current) {
        const patchedMessage = await patchMessage(messageIdRef.current, {
          phase,
          subject_de: subjectDe,
          subject_en: subjectEn,
          html_de: htmlDe,
          html_en: htmlEn,
          text_de: htmlToText(htmlDe),
          text_en: htmlToText(htmlEn),
        });
        if (patchedMessage) {
          setMessage(patchedMessage);
          setMessages((prev) => prev.map((item) => (item.id === patchedMessage.id ? patchedMessage : item)));
        }
      }
      if (persistGen.current === gen) {
        dirtyRef.current = false;
      }
      setSaveState("saved");
      await refreshPreview(messageIdRef.current);
      return persistGen.current === gen;
    } catch (err) {
      setSaveState("error");
      setSaveError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
      return false;
    }
  }, [
    htmlDe,
    htmlEn,
    id,
    phase,
    placeholders,
    placeholdersEn,
    refreshPreview,
    senderId,
    subjectDe,
    subjectEn,
    title,
    allOccupied,
    selectedShorts,
    sourceSlug,
    event?.audience_snapshot_id,
  ]);

  useEffect(() => {
    let cancelled = false;
    llmStatus()
      .then((status) => {
        if (!cancelled) setLlmAvailable(status.available);
      })
      .catch(() => {
        if (!cancelled) setLlmAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoadError(null);
    Promise.all([
      getEvent(id),
      listSenders(),
      listTemplates(),
      listDemoAudiences(),
      listSources().catch(() => [] as ImportSource[]),
    ])
      .then(async ([loaded, senderItems, templateItems, audienceItems, sourceItems]) => {
        if (cancelled) return;
        setSenders(senderItems);
        setTemplates(templateItems);
        setAudiences(audienceItems);
        setSources(sourceItems);
        const stored = loaded.audience_filter?.source;
        const fallback = sourceItems.find((item) => item.default) ?? sourceItems.find((item) => item.enabled);
        setSourceSlug(stored || fallback?.slug || "");
        const terminal = loaded.status === "closed" || loaded.status === "cancelled";
        if (!loaded.messages?.length && !terminal) {
          const created = await createMessage(loaded.id, blankMessage(defaultPhaseForType(loaded.type)));
          loaded.messages = [created];
        }
        if (!cancelled) {
          const wanted = wantedMessageRef.current;
          const wantedId = wanted && loaded.messages?.some((item) => item.id === wanted) ? wanted : undefined;
          applyEvent(loaded, wantedId);
          const first = wantedId
            ? loaded.messages?.find((item) => item.id === wantedId)
            : loaded.messages?.[0];
          if (first) {
            const full = await getMessage(first.id);
            if (!cancelled) {
              setMessage(full);
              setMessages((prev) => prev.map((item) => (item.id === full.id ? full : item)));
              setTarget(full.state === "draft" ? audienceIdOf(full, audienceItems) : "one");
            }
            if (first.origin !== "legacy_import") await refreshPreview(first.id);
          }
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(err instanceof ApiError ? err.message : "Mail konnte nicht geladen werden.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [applyEvent, id, refreshPreview]);

  const persistRef = useRef(persist);
  persistRef.current = persist;

  useEffect(() => {
    if (!id) return;
    const timer = window.setTimeout(() => {
      void previewAudience(id, {
        source: sourceSlug || null,
        rules: allOccupied ? [] : selectedShorts,
      })
        .then((next) => {
          setAudiencePreview(next);
          setAudienceError(null);
        })
        .catch((err: unknown) => {
          setAudiencePreview(null);
          setAudienceError(
            err instanceof ApiError
              ? err.code === "directory_not_synced"
                ? "Quelle noch nicht synchronisiert. Unter Quellen einmal syncen, solange die Quelldatenbank erreichbar ist."
                : err.message
              : "Empfängervorschau fehlgeschlagen.",
          );
        });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [id, allOccupied, selectedShorts, sourceSlug]);

  useEffect(() => {
    if (skipDirtyRef.current) {
      skipDirtyRef.current = false;
      return;
    }
    if (!messageIdRef.current) return;
    dirtyRef.current = true;
    persistGen.current += 1;
    if (saveTimer.current) {
      window.clearTimeout(saveTimer.current);
    }
    saveTimer.current = window.setTimeout(() => {
      void persistRef.current();
    }, 700);
    return () => {
      if (saveTimer.current) {
        window.clearTimeout(saveTimer.current);
      }
    };
  }, [
    title,
    placeholders,
    placeholdersEn,
    senderId,
    phase,
    subjectDe,
    subjectEn,
    htmlDe,
    htmlEn,
    allOccupied,
    selectedShorts,
  ]);

  async function selectMessage(next: Message) {
    if (dirtyRef.current) {
      await persist();
    }
    skipDirtyRef.current = true;
    messageIdRef.current = next.id;
    setMessage(next);
    setPhase(next.phase);
    setSubjectDe(next.subject_de);
    setSubjectEn(next.subject_en);
    setHtmlDe(next.html_de);
    setHtmlEn(next.html_en);
    setSendResult(null);
    setSendError(null);
    setRefineDe(EMPTY_REFINE);
    setRefineEn(EMPTY_REFINE);
    setTranslateState(EMPTY_TRANSLATE);
    setTarget(next.state === "draft" ? audienceIdOf(next) : "one");
    try {
      const full = await getMessage(next.id);
      setMessage(full);
      setMessages((prev) => prev.map((item) => (item.id === full.id ? full : item)));
      setTarget(full.state === "draft" ? audienceIdOf(full) : "one");
    } catch {
      /* list payload is enough to edit */
    }
    if (next.origin !== "legacy_import") await refreshPreview(next.id);
  }

  async function addMessage(nextPhase: MessagePhase, templateId?: string) {
    if (!event) return;
    try {
      if (dirtyRef.current) {
        await persist();
      }
      const created = await createMessage(
        event.id,
        templateId
          ? { ...blankMessage(nextPhase), template_id: templateId }
          : blankMessage(nextPhase),
      );
      const refreshed = await getEvent(event.id);
      applyEvent(refreshed, created.id);
      await refreshPreview(created.id);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Nachricht konnte nicht angelegt werden.");
    }
  }

  async function handleSaveTemplate() {
    if (!message) return;
    const title = window.prompt("Name der Vorlage", message.subject_de || event?.title || "Vorlage");
    if (!title?.trim()) return;
    try {
      const saved = await saveMessageAsTemplate(message.id, title.trim());
      setTemplates((prev) => [...prev, saved]);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Vorlage konnte nicht gespeichert werden.");
    }
  }

  async function handleDraftFrom() {
    if (!message) return;
    try {
      const draft = await draftFromMessage(message.id);
      navigate(`/events/${draft.event_id}?message=${draft.id}`);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Entwurf konnte nicht angelegt werden.");
    }
  }

  async function handleSend(sendTarget: "one" | "demo" | "demo-gmail" | "filter" = target) {
    if (!message) return;
    if (placeholderGaps.length) {
      setSendError(
        `Noch offen: ${placeholderGaps.map((name) => `{{${name}}}`).join(", ")}. Feld füllen oder den Platzhalter aus dem Text nehmen.`,
      );
      return;
    }
    if (saveTimer.current) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    if (dirtyRef.current) {
      const saved = await persist();
      if (!saved) {
        setSendError("Bitte zuerst speichern.");
        return;
      }
    }
    // From a draft, one address is a test mail: the draft stays editable and sendable.
    const test = sendTarget === "one" && !locked;
    const bulk = sendTarget !== "one";
    const expected = sendTargetInfo(sendTarget).count;
    setSending(true);
    setSendError(null);
    setSendResult(null);
    setSendNotice(
      bulk
        ? `Versand wird vorbereitet${expected ? ` – ${expected} Empfänger` : ""} … bitte warten, nicht erneut senden.`
        : null,
    );
    try {
      const result = await sendMessage(message.id, {
        address: sendTarget === "one" ? address.trim() : null,
        audience: sendTarget === "one" ? null : sendTarget,
        list_id_short: sendTarget === "one" ? listIdShort.trim() || null : null,
        scheduled_for:
          locked || test || !scheduledAt ? null : new Date(scheduledAt).toISOString(),
        test,
      });
      const n = result.deliveries?.length ?? 0;
      if (test) {
        setSendResult("Testmail in der Queue — der Entwurf bleibt offen.");
      } else if (result.state === "scheduled") {
        setSendResult(
          n > 1
            ? `${n} Kopien geplant — siehe Queue.`
            : "Geplant — Versand beginnt zum gesetzten Zeitpunkt.",
        );
      } else if (n > 1) {
        setSendResult(`${n} Kopien in der Queue.`);
      } else {
        setSendResult(locked ? "Weitergeleitet." : "Gesendet.");
      }
      if (id) {
        const refreshed = await getEvent(id);
        applyEvent(refreshed, message.id);
      }
      setMessage(result);
      setMessages((prev) => prev.map((item) => (item.id === result.id ? result : item)));
    } catch (err) {
      setSendError(sendErrorText(err));
    } finally {
      setSending(false);
      setSendNotice(null);
    }
  }

  // While copies are going out, keep progress and state fresh without a reload.
  const messageState = message?.state;
  const messageId = message?.id;
  useEffect(() => {
    if (!id || !messageId || messageState !== "sending") return;
    const timer = window.setInterval(() => {
      void getEvent(id)
        .then((next) => {
          setEvent((prev) =>
            prev ? { ...prev, status: next.status, delivery_progress: next.delivery_progress } : prev,
          );
          const fresh = (next.messages ?? []).find((item) => item.id === messageId);
          if (fresh && fresh.state !== messageState) {
            setMessage((prev) => (prev && prev.id === messageId ? { ...prev, state: fresh.state } : prev));
            setMessages((prev) =>
              prev.map((item) => (item.id === messageId ? { ...item, state: fresh.state } : item)),
            );
          }
        })
        .catch(() => undefined);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [id, messageId, messageState]);

  function sendTargetInfo(sendTarget: "one" | "demo" | "demo-gmail" | "filter") {
    if (sendTarget === "one") {
      return { label: address.trim() || "—", count: undefined as number | undefined };
    }
    if (sendTarget === "filter") {
      return {
        label: audiencePreview?.label ?? event?.audience_label ?? "Gefilterte Empfänger",
        count: audiencePreview?.recipient_count,
      };
    }
    const demo = audiences.find((item) => item.id === sendTarget);
    return { label: demo?.title ?? sendTarget, count: demo?.count };
  }

  async function handleClose() {
    if (!event) return;
    setResolving(true);
    setSaveError(null);
    try {
      const next = await closeEvent(event.id);
      applyEvent(next, messageIdRef.current ?? undefined);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Abschließen fehlgeschlagen.");
    } finally {
      setResolving(false);
    }
  }

  async function handleCancel() {
    if (!event) return;
    setResolving(true);
    setSaveError(null);
    try {
      const next = await cancelEvent(event.id);
      applyEvent(next, messageIdRef.current ?? undefined);
      navigate("/");
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Abbrechen fehlgeschlagen.");
    } finally {
      setResolving(false);
    }
  }

  async function handleCancelSchedule() {
    if (!message?.scheduled_for) return;
    const n = (message.deliveries ?? []).filter((delivery) => delivery.state === "deferred").length;
    if (!window.confirm(`Geplanten Versand (${n} Kopien, ${formatDateTime(message.scheduled_for)}) abbrechen?`)) return;
    setSending(true);
    setSendError(null);
    setSendNotice(null);
    setSendResult(null);
    try {
      const result = await cancelScheduledSend(message.id);
      setSendResult("Geplanter Versand abgebrochen.");
      if (id) applyEvent(await getEvent(id), message.id);
      setMessage(result);
      setMessages((prev) => prev.map((item) => (item.id === result.id ? result : item)));
    } catch (err) {
      setSendError(err instanceof ApiError ? err.message : "Abbrechen fehlgeschlagen.");
    } finally {
      setSending(false);
    }
  }

  async function handleDiscardMessage() {
    if (!event || !message) return;
    const label = message.subject_de || message.subject_en || "diesen Entwurf";
    if (!window.confirm(`Entwurf „${label}“ verwerfen? Das lässt sich nicht rückgängig machen.`)) return;
    setResolving(true);
    setSaveError(null);
    try {
      if (saveTimer.current) {
        window.clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      if (dirtyRef.current) await persist();
      const next = await discardMessage(event.id, message.id);
      applyEvent(next);
      const selected = next.messages?.find((item) => item.state === "draft") ?? next.messages?.[0];
      if (selected) await refreshPreview(selected.id);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Entwurf konnte nicht verworfen werden.");
    } finally {
      setResolving(false);
    }
  }

  const usedPlaceholderKeys = useMemo(
    () => new Set(placeholderNames(`${subjectDe}\n${subjectEn}\n${htmlDe}\n${htmlEn}`)),
    [htmlDe, htmlEn, subjectDe, subjectEn],
  );
  // Once the text was translated, a DE value without EN counterpart means it changed afterwards.
  const staleEnFields = useMemo(() => {
    if (!Object.keys(nonEmptyValues(placeholdersEn)).length) return [];
    const usedInEn = new Set(placeholderNames(`${subjectEn}\n${htmlEn}`));
    return PH_FIELDS.filter(
      (field) =>
        usedInEn.has(field.name) &&
        placeholders[field.name]?.trim() &&
        !placeholdersEn[field.name]?.trim() &&
        !englishValue(field.name, placeholders[field.name]),
    );
  }, [htmlEn, placeholders, placeholdersEn, subjectEn]);
  const enValues = useMemo(
    () => englishValues(placeholders, placeholdersEn),
    [placeholders, placeholdersEn],
  );
  const subjectGaps = [
    ...new Set([
      ...unresolvedPlaceholderNames(subjectDe, placeholders),
      ...unresolvedPlaceholderNames(subjectEn, enValues),
    ]),
  ];
  // Every {{key}} still open in subject or body; the backend rejects such a send too.
  const placeholderGaps = [
    ...new Set([
      ...unresolvedPlaceholderNames(`${subjectDe}\n${htmlDe}`, placeholders),
      ...unresolvedPlaceholderNames(`${subjectEn}\n${htmlEn}`, enValues),
    ]),
  ];
  const gapLabels = placeholderGaps.map(
    (name) => (PLACEHOLDER_LABEL as Record<string, string>)[name] ?? `{{${name}}}`,
  );
  const sendBlockedHint = placeholderGaps.length
    ? `Offene Platzhalter: ${placeholderGaps.map((name) => `{{${name}}}`).join(", ")}`
    : undefined;
  // Warnings only; a past date is normal in an incident, so that check is for maintenance.
  const dateHints = dateWarnings(placeholders.date ?? "", { checkPast: event?.type === "maintenance" });
  const startInPast = !locked && isPastStart(scheduledAt);
  const scheduledDate = fromLocalValue(scheduledAt);
  const maintenanceBegin =
    event?.type === "maintenance" ? maintenanceStart(placeholders.date, placeholders.start) : null;
  const previewSubject = `+++ ${substitutePlaceholders(subjectDe, placeholders, { highlightUnset: false })} +++ ${substitutePlaceholders(subjectEn, enValues, { highlightUnset: false })} +++`;

  const previewText = useMemo(() => {
    const de = substitutePlaceholders(htmlToText(htmlDe), placeholders, { highlightUnset: false });
    const en = substitutePlaceholders(htmlToText(htmlEn), enValues, { highlightUnset: false });
    const body = [de, en].filter(Boolean).join("\n\n----------\n\n");
    const sender = senders.find((item) => item.id === senderId);
    if (!body) return "";
    if (sender?.text_signature) {
      return `${body}\n\n-- \n${sender.text_signature}`;
    }
    return body;
  }, [htmlDe, htmlEn, placeholders, enValues, senders, senderId]);

  if (loadError) {
    return <div className="px-4 py-6 text-sm text-danger">{loadError}</div>;
  }
  if (!event) {
    return (
      <div className="grid flex-1 grid-cols-2 gap-3 p-4">
        <div className="animate-pulse rounded-lg bg-surface-subtle" />
        <div className="animate-pulse rounded-lg bg-surface-subtle" />
      </div>
    );
  }
  if (!message) {
    return (
      <div className="px-4 py-8 text-sm text-muted">
        Diese Mail hat keinen Entwurf ({eventStatusLabel(event)}).
      </div>
    );
  }

  if (message.origin === "legacy_import") {
    return (
      <LegacyMailView
        event={event}
        messages={messages}
        message={message}
        saveError={saveError}
        onSelect={(item) => void selectMessage(item)}
        onSaveTemplate={() => void handleSaveTemplate()}
        onDraft={() => void handleDraftFrom()}
      />
    );
  }

  const usedFields = PH_FIELDS.filter((field) => usedPlaceholderKeys.has(field.name));
  const unusedFields = PH_FIELDS.filter((field) => !usedPlaceholderKeys.has(field.name));
  const openFields = usedFields.filter((field) => !placeholders[field.name]?.trim());
  const sourceDe = substitutePlaceholders(htmlDe, placeholders, { highlightUnset: false });
  const sourceEn = substitutePlaceholders(htmlEn, enValues, { highlightUnset: false });
  const recipientCount = audiencePreview?.recipient_count ?? 0;

  function focusField(name: string) {
    const node = phRefs.current[name];
    if (node) {
      node.focus();
      node.select();
      return;
    }
    setShowAllFields(true);
    window.setTimeout(() => {
      phRefs.current[name]?.focus();
      phRefs.current[name]?.select();
    }, 0);
  }

  function fieldInput(field: { name: string; label: string }) {
    const missing = usedPlaceholderKeys.has(field.name) && !placeholders[field.name]?.trim();
    return (
      <div key={field.name}>
        <label className="field-label" htmlFor={`ed-${field.name}`}>
          {field.label} <span className="font-mono text-[10px]">{`{{${field.name}}}`}</span>
        </label>
        <input
          id={`ed-${field.name}`}
          ref={(node) => {
            phRefs.current[field.name] = node;
          }}
          className={cn(
            "field",
            missing ? "border-danger" : field.name === "date" && dateHints.length > 0 && "border-amber",
          )}
          value={placeholders[field.name] ?? ""}
          onChange={(e) => {
            setPlaceholders((prev) => ({ ...prev, [field.name]: e.target.value }));
            if (field.name === "provider") return; // has its own English field below
            // A translated value no longer matches the edited German one; drop it so the
            // EN block falls back visibly (see staleEnFields) instead of staying silently wrong.
            setPlaceholdersEn((prev) => {
              if (!(field.name in prev)) return prev;
              const { [field.name]: _dropped, ...rest } = prev;
              return rest;
            });
          }}
        />
        {field.name === "provider" ? (
          <input
            aria-label="Netzbetreiber englisch"
            className="field mt-1 text-[12px]"
            value={placeholdersEn.provider ?? ""}
            placeholder={
              placeholders.provider?.trim()
                ? `englisch, leer: „${placeholders.provider.trim()}“`
                : "englisch (optional)"
            }
            onChange={(e) => setPlaceholdersEn((prev) => ({ ...prev, provider: e.target.value }))}
          />
        ) : null}
        {field.name !== "provider" && enValues[field.name] && enValues[field.name] !== placeholders[field.name] ? (
          <p className="mt-0.5 truncate text-[11px] text-muted" title="Wert im englischen Teil">
            EN: {enValues[field.name]}
          </p>
        ) : null}
        {field.name === "date" && dateHints.length ? (
          <p className="mt-0.5 text-[11px] text-amber">{dateHints.join(" ")}</p>
        ) : null}
      </div>
    );
  }

  // Narrow (side panel): name above value, so the value gets the full width.
  // Values wrap only where they must (overflow-wrap: anywhere), not mid-word everywhere.
  const headerList = (narrow: boolean) => {
    const row = narrow ? "space-y-0.5" : "grid grid-cols-[7rem_1fr] gap-2";
    const name = narrow ? "font-sans text-[10px] uppercase tracking-wide text-muted" : "text-muted";
    return (
      <dl
        className={cn(
          "rounded-md border border-hairline bg-bg font-mono",
          narrow ? "space-y-2 px-2.5 py-2 text-xs" : "space-y-1 px-3 py-2 text-[11px]",
        )}
      >
        {PREVIEW_HEADERS.map((header) => (
          <div key={header} className={row}>
            <dt className={name}>{header}</dt>
            <dd className="text-ink [overflow-wrap:anywhere]">{preview?.headers?.[header] ?? "—"}</dd>
          </div>
        ))}
        <div className={row}>
          <dt className={name}>Subject</dt>
          <dd className={cn("[overflow-wrap:anywhere]", subjectGaps.length ? "text-danger" : "text-ink")}>
            {previewSubject}
          </dd>
        </div>
      </dl>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-hairline bg-surface px-4 py-2">
        <input
          className="field max-w-xs font-medium"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          aria-label="Titel"
        />
        <Badge tone={typeTone(event.type)}>{EVENT_TYPE_LABEL[event.type]}</Badge>
        <Badge tone={statusTone(event.status)}>{eventStatusLabel(event)}</Badge>
        <DeliveryProgressBar progress={event.delivery_progress} className="w-40" />
        <span className="text-[11px] text-muted">
          {saveState === "saving"
            ? "Speichern…"
            : saveState === "saved"
              ? "Gespeichert"
              : saveState === "error"
                ? "Speichern fehlgeschlagen"
                : ""}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <div className="flex overflow-hidden rounded-md border border-hairline">
            {(
              [
                ["doc", "Wie versendet"],
                ["mail", "Mailansicht"],
                ["source", "Quelltext"],
                ["text", "Nur Text"],
              ] as const
            ).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                aria-pressed={docMode === mode}
                onClick={() => setDocMode(mode)}
                className={cn(
                  "px-2 py-1 text-xs",
                  docMode === mode ? "bg-accent-dim text-accent" : "text-muted hover:bg-surface-subtle",
                )}
              >
                {label}
              </button>
            ))}
          </div>
          {isOpenEvent(event.status) && event.type !== "info" ? (
            <Button size="sm" variant="primary" disabled={resolving} onClick={() => void handleClose()}>
              Als erledigt markieren
            </Button>
          ) : null}
          {isOpenEvent(event.status) && event.type === "info" && messages.some((item) => item.state === "draft") ? (
            <Button size="sm" disabled={resolving} onClick={() => void handleClose()}>
              Ohne Versand erledigen
            </Button>
          ) : null}
          {event.status !== "closed" && event.status !== "cancelled" ? (
            <Button size="sm" variant="ghost" disabled={resolving} onClick={() => void handleCancel()}>
              {event.status === "draft" ? "Verwerfen" : "Abbrechen"}
            </Button>
          ) : null}
          {message.state !== "draft" ? (
            <Button
              size="sm"
              title="Neuer Vorgang mit einer Kopie dieser Mail; Datum und Uhrzeiten bleiben leer"
              onClick={() => void handleDraftFrom()}
            >
              Als Entwurf
            </Button>
          ) : null}
          <Button size="sm" onClick={() => void handleSaveTemplate()}>
            Als Vorlage
          </Button>
          <Button size="sm" onClick={() => void persist()}>
            Speichern
          </Button>
          {message.state === "draft" && messages.length > 1 ? (
            <Button size="sm" variant="danger" disabled={resolving} onClick={() => void handleDiscardMessage()}>
              Entwurf verwerfen
            </Button>
          ) : null}
        </div>
      </div>
      {saveError ? <p className="border-b border-hairline px-4 py-1.5 text-xs text-danger">{saveError}</p> : null}

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <div className="min-h-0 flex-1 overflow-auto bg-bg p-4">
          {docMode === "doc" ? (
            <div className="mx-auto w-full max-w-[680px]">
              <div className="mb-3 rounded-lg border border-hairline bg-surface px-3 py-2">
                <span className="field-label">Betreff</span>
                <div className="flex flex-wrap items-center gap-1 font-mono text-xs">
                  <span className="text-muted">+++</span>
                  <SubjectField
                    className="text-xs"
                    value={subjectDe}
                    onChange={setSubjectDe}
                    disabled={locked}
                    placeholder="Betreff deutsch"
                    aria-label="Betreff deutsch"
                    values={placeholders}
                    fields={PH_FIELDS}
                    onPickPlaceholder={focusField}
                  />
                  <span className="text-muted">+++</span>
                  {showEn ? (
                    <>
                      <SubjectField
                        className="text-xs"
                        value={subjectEn}
                        onChange={setSubjectEn}
                        disabled={locked}
                        placeholder="subject english"
                        aria-label="Betreff englisch"
                        values={enValues}
                        fields={PH_FIELDS}
                        onPickPlaceholder={focusField}
                      />
                      <span className="text-muted">+++</span>
                    </>
                  ) : null}
                </div>
                {subjectGaps.length ? (
                  <p className="mt-1 text-[11px] text-danger">
                    Offener Platzhalter im Betreff:{" "}
                    {subjectGaps.map((name) => `{{${name}}}`).join(", ")} — rechts füllen oder aus dem
                    Betreff nehmen. Sonst geht das so raus.
                  </p>
                ) : (
                  <p className="mt-1 truncate text-[11px] text-muted" title={previewSubject}>
                    {previewSubject}
                  </p>
                )}
              </div>

              <div className="rounded-lg border border-hairline bg-surface px-5 py-4">
                <HtmlEditor
                  value={htmlDe}
                  onChange={handleHtmlDeChange}
                  disabled={locked}
                  placeholder="Deutsche Fassung…"
                  values={placeholders}
                  fields={PH_FIELDS}
                  onPickPlaceholder={focusField}
                  toolbarExtra={refineControls("de")}
                />
                <div className="relative my-5 border-t border-dashed border-hairline text-center">
                  <span className="relative -top-2 bg-surface px-2 font-mono text-[10px] uppercase tracking-wide text-muted">
                    English version
                  </span>
                  {llmAvailable ? (
                    <div className="relative top-1 flex flex-wrap items-center justify-center gap-2">
                      {!translateState.confirm ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={locked || emptyHtml(htmlDe) || translateState.loading}
                          onClick={handleTranslateClick}
                          className="text-[11px]"
                        >
                          {translateState.loading
                            ? "Übersetze…"
                            : translateValuesOnly
                              ? "Platzhalter-Werte übersetzen"
                              : "Aus dem Deutschen übersetzen"}
                        </Button>
                      ) : (
                        <>
                          <span className="text-[11px] text-amber">Englische Fassung ersetzen?</span>
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={translateState.loading}
                            onClick={() => void runTranslate()}
                            className="text-[11px]"
                          >
                            {translateState.loading ? "Übersetze…" : "Ersetzen"}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setTranslateState((prev) => ({ ...prev, confirm: false }))}
                            className="text-[11px]"
                          >
                            Abbrechen
                          </Button>
                        </>
                      )}
                      {translateState.undo ? (
                        <Button size="sm" variant="ghost" onClick={handleUndoTranslate} className="text-[11px]">
                          ↩ Rückgängig
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-expanded={enOpen}
                        onClick={() => setEnOpen((open) => !open)}
                        className="text-[11px]"
                      >
                        {enOpen ? "Englisch ausblenden" : "Englisch anzeigen"}
                      </Button>
                    </div>
                  ) : null}
                  {translateState.error ? (
                    <p className="relative top-1 text-[11px] text-danger">{translateState.error}</p>
                  ) : null}
                  {staleEnFields.length ? (
                    <p className="relative top-1 mx-auto max-w-[24rem] text-[11px] text-amber">
                      ⚠ Geändert nach der Übersetzung: {staleEnFields.map((field) => field.label).join(", ")} — im
                      englischen Teil steht bis zur nächsten Übersetzung der deutsche Wert.
                    </p>
                  ) : null}
                  {translateState.warnings.length ? (
                    <ul className="relative top-1 mx-auto max-w-[24rem] space-y-0.5 text-left text-[11px] text-amber">
                      {translateState.warnings.map((warning, index) => (
                        <li key={index}>⚠ {warning}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                {showEn ? (
                  <HtmlEditor
                    value={htmlEn}
                    onChange={handleHtmlEnChange}
                    disabled={locked}
                    placeholder="English version…"
                    values={enValues}
                    fields={PH_FIELDS}
                    onPickPlaceholder={focusField}
                    toolbarExtra={refineControls("en")}
                  />
                ) : null}
                {emptyHtml(htmlEn) ? (
                  <p className="mt-2 text-[11px] text-amber">
                    Noch keine englische Fassung — die Mail geht sonst einsprachig raus.
                  </p>
                ) : null}
              </div>
              {previewError ? <p className="mt-2 text-xs text-danger">{previewError}</p> : null}
            </div>
          ) : null}

          {docMode === "mail" ? (
            <div className="mx-auto w-full max-w-[680px] space-y-3">
              {headerList(false)}
              <p className="text-[11px] text-muted">
                Fertige Mail im StudNet-Layout, Stand der letzten Speicherung.
              </p>
              {preview?.html ? (
                <iframe
                  title="Mailansicht"
                  srcDoc={preview.html}
                  // No scripts; same origin only so the frame can be sized to its content.
                  sandbox="allow-same-origin allow-popups"
                  className="block w-full rounded-lg border border-hairline bg-white"
                  style={{ height: mailFrameHeight }}
                  onLoad={(e) => {
                    const doc = e.currentTarget.contentDocument;
                    if (doc) setMailFrameHeight(doc.documentElement.scrollHeight + 4);
                  }}
                />
              ) : (
                <p className="text-xs text-muted">{previewError ?? "Noch keine Vorschau — erst speichern."}</p>
              )}
            </div>
          ) : null}

          {docMode === "source" ? (
            <div className="mx-auto w-full max-w-[680px] space-y-3">
              {headerList(false)}
              <p className="text-[11px] text-muted">
                Platzhalter sind hier schon ersetzt — das ist der Stand, den der Versand baut.
              </p>
              <div>
                <span className="field-label">HTML DE</span>
                <pre className="whitespace-pre-wrap rounded-md border border-hairline bg-surface p-3 font-mono text-[11px] [overflow-wrap:anywhere]">
                  {sourceDe ? formatHtmlSource(sourceDe) : "—"}
                </pre>
              </div>
              <div>
                <span className="field-label">HTML EN</span>
                <pre className="whitespace-pre-wrap rounded-md border border-hairline bg-surface p-3 font-mono text-[11px] [overflow-wrap:anywhere]">
                  {sourceEn ? formatHtmlSource(sourceEn) : "—"}
                </pre>
              </div>
            </div>
          ) : null}

          {docMode === "text" ? (
            <div className="mx-auto w-full max-w-[680px]">
              <pre className="whitespace-pre-wrap rounded-lg border border-hairline bg-surface p-4 font-mono text-xs">
                {preview?.text || previewText || "Plaintext entsteht aus dem HTML."}
              </pre>
            </div>
          ) : null}
        </div>

        <aside className="flex w-full shrink-0 flex-col gap-4 overflow-auto border-t border-hairline bg-surface p-3 lg:w-64 lg:border-l lg:border-t-0">
          <section>
            <div className="mb-1 flex items-baseline justify-between gap-2">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Eckdaten</h2>
              <span className="font-mono text-[10px] text-muted">
                {usedFields.length - openFields.length}/{usedFields.length}
              </span>
            </div>
            {usedFields.length ? (
              <div className="space-y-2">{usedFields.map(fieldInput)}</div>
            ) : (
              <p className="text-[11px] text-muted">Dieser Text benutzt keine Platzhalter.</p>
            )}
            {unusedFields.length ? (
              showAllFields ? (
                <div className="mt-2 space-y-2 border-t border-hairline pt-2">
                  <p className="text-[10px] text-muted">Kommen im Text nicht vor:</p>
                  {unusedFields.map(fieldInput)}
                </div>
              ) : (
                <button
                  type="button"
                  className="mt-2 text-[11px] text-accent underline underline-offset-2"
                  onClick={() => setShowAllFields(true)}
                >
                  + {unusedFields.length} weitere Felder
                </button>
              )
            ) : null}
          </section>

          {event.type !== "info" ? (
            <section>
              <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Diese Mail</h2>
              <p className="text-sm font-medium">{MESSAGE_PHASE_LABEL[phase]}</p>
              <p className="text-[11px] text-muted">{MESSAGE_PHASE_HINT[phase]}</p>
              {locked ? null : (
                <select
                  id="ed-phase"
                  className="field mt-1"
                  value={phase}
                  aria-label="Art dieser Mail"
                  onChange={(e) => setPhase(e.target.value as MessagePhase)}
                >
                  {phasesForType(event.type).map((value) => (
                    <option key={value} value={value}>
                      {MESSAGE_PHASE_LABEL[value]}
                    </option>
                  ))}
                </select>
              )}
            </section>
          ) : null}

          <section>
            <label className="field-label" htmlFor="ed-sender">
              Absender
            </label>
            <select id="ed-sender" className="field" value={senderId} onChange={(e) => setSenderId(e.target.value)}>
              <option value="">— keiner —</option>
              {senders.map((sender) => (
                <option key={sender.id} value={sender.id}>
                  {sender.display_name}
                </option>
              ))}
            </select>
          </section>

          {messages.length > 1 ? (
            <section>
              <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">
                {event.type === "info" ? "Mails zu diesem Thema" : "Mails in diesem Vorgang"}
              </h2>
              <div className="flex flex-col gap-1">
                {messages.map((item, index) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => void selectMessage(item)}
                    className={cn(
                      "rounded-md border px-2 py-1 text-left text-xs",
                      item.id === message.id
                        ? "border-accent bg-accent-dim text-accent"
                        : "border-hairline text-muted hover:bg-surface-subtle",
                    )}
                  >
                    {event.type === "info" ? `Mail ${index + 1}` : MESSAGE_PHASE_LABEL[item.phase]}
                    {" · "}
                    {MESSAGE_STATE_LABEL[item.state] ?? item.state}
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          {event.status !== "cancelled" &&
          (event.type !== "info" ||
            (messages.some((item) => item.state === "sent") &&
              !messages.some((item) => item.state === "draft"))) ? (
            <section className="rounded-md border border-hairline bg-bg p-2">
              <p className="text-xs font-semibold text-ink">
                {event.type === "info"
                  ? "Update zu dieser Mail"
                  : event.status === "closed"
                    ? "Vorgang wieder öffnen"
                    : "Weitere Mail schreiben"}
              </p>
              <div className="mt-2 flex flex-col gap-1.5">
                {(event.type === "info" ? (["adhoc"] as MessagePhase[]) : phasesForType(event.type)).map(
                  (value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => void addMessage(value)}
                      className="flex flex-col rounded-md border border-hairline px-2 py-1.5 text-left hover:bg-surface-subtle"
                    >
                      <span className="text-xs font-medium">
                        {event.type === "info" ? "Update schreiben" : MESSAGE_PHASE_LABEL[value]}
                      </span>
                      <span className="text-[10px] text-muted">{MESSAGE_PHASE_HINT[value]}</span>
                    </button>
                  ),
                )}
              </div>
              {templates.filter((item) => !item.event_type || item.event_type === event.type).length > 0 ? (
                <div className="mt-2 border-t border-hairline pt-2">
                  <p className="mb-1 text-[10px] text-muted">Oder aus Vorlage</p>
                  <div className="flex flex-col gap-1">
                    {templates
                      .filter((item) => !item.event_type || item.event_type === event.type)
                      .map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => void addMessage(template.phase, template.id)}
                          className="rounded-md px-2 py-1 text-left text-xs text-accent hover:bg-surface-subtle"
                        >
                          {template.title}
                        </button>
                      ))}
                  </div>
                </div>
              ) : null}
            </section>
          ) : null}

          <details className="rounded-md border border-hairline bg-bg px-2 py-1.5">
            <summary className="cursor-pointer text-xs font-medium text-muted">Kopfzeilen</summary>
            <div className="mt-2">{headerList(true)}</div>
          </details>
        </aside>
      </div>

      {sendNotice || sendResult || sendError ? (
        <div
          role={sendError ? "alert" : "status"}
          className={cn(
            "flex flex-wrap items-center gap-2 border-t px-4 py-2.5 text-sm font-medium",
            sendError
              ? "border-danger bg-danger/10 text-danger"
              : sendNotice
                ? "border-accent bg-accent/10 text-accent"
                : "border-green bg-green/10 text-green",
          )}
        >
          {sendNotice ? (
            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : null}
          <span>{sendError ?? sendNotice ?? sendResult}</span>
        </div>
      ) : null}

      <div className="relative flex flex-wrap items-center gap-2 border-t border-hairline bg-surface px-4 py-2">
        {locked ? (
          <>
            <Badge tone={message.state === "sent" ? "success" : "accent"}>
              {MESSAGE_STATE_LABEL[message.state] ?? message.state}
            </Badge>
            {message.state === "sending" && event.delivery_progress?.total ? (
              <span className="text-xs font-medium text-accent">
                {event.delivery_progress.submitted} von {event.delivery_progress.total} Kopien raus
                {event.delivery_progress.failed ? ` · ${event.delivery_progress.failed} fehlgeschlagen` : ""}
              </span>
            ) : null}
            {sentAudience || message.audience === "filter" ? (
              <span className="text-xs text-muted">
                {message.state === "sent" ? "Ging an" : "Geht an"}{" "}
                <strong className="text-ink">
                  {sentAudience?.title ?? event.audience_label ?? "Gefilterte Empfänger"}
                </strong>
                {sentAudience
                  ? ` (${sentAudience.count})`
                  : audiencePreview
                    ? ` (${audiencePreview.recipient_count})`
                    : ""}
              </span>
            ) : null}
            {(message.deliveries ?? []).length > 1 ? (
              <span className="text-[11px] text-muted">
                {(message.deliveries ?? []).length} Kopien — Details in der Queue
              </span>
            ) : (
              (message.deliveries ?? []).slice(0, 1).map((delivery) => (
                <span key={delivery.id} className="font-mono text-[11px] text-muted">
                  {delivery.address} · {delivery.rfc_message_id ?? delivery.state}
                </span>
              ))
            )}
            {(() => {
              const bounced = (message.deliveries ?? []).filter((delivery) => delivery.bounced_at);
              if (bounced.length === 0) return null;
              const reasons = [...new Set(bounced.map((delivery) => bounceCategoryLabel(delivery.bounce_category)))];
              return (
                <Badge tone="warn" title={bounced.map((delivery) => delivery.address).join("\n")}>
                  {bounced.length === 1 ? "Rückläufer" : `${bounced.length} Rückläufer`}: {reasons.join(", ")}
                </Badge>
              );
            })()}
            {message.state === "scheduled" && message.scheduled_for ? (
              <>
                <span className="text-[11px] text-muted">
                  Versand beginnt {formatDateTime(message.scheduled_for)}
                </span>
                <Button size="sm" disabled={sending} onClick={() => void handleCancelSchedule()}>
                  Geplanten Versand abbrechen
                </Button>
              </>
            ) : null}
            <div className="ml-auto flex items-center gap-1.5">
              {sentAudience ? (
                <Button
                  size="sm"
                  disabled={sending || placeholderGaps.length > 0}
                  title={sendBlockedHint}
                  onClick={() => setPendingSend(sentAudience.id)}
                >
                  Noch einmal an {sentAudience.title}
                </Button>
              ) : null}
              {message.audience === "filter" ? (
                <Button
                  size="sm"
                  disabled={sending || placeholderGaps.length > 0}
                  title={sendBlockedHint}
                  onClick={() => setPendingSend("filter")}
                >
                  Noch einmal an {event.audience_label ?? "gefilterte Empfänger"}
                </Button>
              ) : null}
              <div className="relative">
                <Button size="sm" onClick={() => setTestOpen((open) => !open)} aria-expanded={testOpen}>
                  Weiterleiten
                </Button>
                {testOpen ? (
                  <div className="absolute bottom-full right-0 z-30 mb-2 w-64 rounded-md border border-hairline bg-surface p-2.5 shadow-xl">
                    <label className="field-label" htmlFor="send-address">
                      Weiterleiten an
                    </label>
                    <input
                      id="send-address"
                      type="email"
                      className="field mb-2"
                      placeholder="ops@example.test"
                      value={address}
                      onChange={(e) => {
                        setTarget("one");
                        setAddress(e.target.value);
                      }}
                    />
                    <label className="field-label" htmlFor="list-id-short">
                      List-Id (Wohnheim-Kürzel, optional)
                    </label>
                    <input
                      id="list-id-short"
                      className="field mb-2"
                      placeholder="ferdi1"
                      value={listIdShort}
                      onChange={(e) => setListIdShort(e.target.value)}
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      className="w-full"
                      disabled={sending || !address.trim() || placeholderGaps.length > 0}
                      title={sendBlockedHint}
                      onClick={() => {
                        setTestOpen(false);
                        setPendingSend("one");
                      }}
                    >
                      {sending ? "Senden…" : "Weiterleiten"}
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
          </>
        ) : event.status === "cancelled" ? (
          <span className="text-xs text-muted">Vorgang abgebrochen — kein Versand mehr möglich.</span>
        ) : (
          <>
            <Button
              ref={audienceButtonRef}
              size="sm"
              aria-expanded={audienceOpen}
              onClick={() => setAudienceOpen((open) => !open)}
              className={cn(audienceError && "border-danger text-danger")}
            >
              Empfänger:{" "}
              {allOccupied
                ? "alle Nutzer"
                : selectedShorts.length === 1
                  ? sourceRules.find((item) => item.slug === selectedShorts[0])?.title ?? selectedShorts[0]
                  : `${selectedShorts.length} Wohnheime`}
              <span className="ml-1 font-mono text-accent">{audiencePreview ? recipientCount : "…"}</span>
            </Button>
            {gapLabels.length ? (
              <span className="text-[11px] text-danger">offen: {gapLabels.join(", ")}</span>
            ) : null}
            {audiencePreview?.stale ? <Badge tone="warn">Sync veraltet</Badge> : null}
            {scheduledDate ? (
              <span
                className={cn(
                  "inline-flex items-center overflow-hidden rounded-md text-xs font-semibold",
                  startInPast ? "bg-[color-mix(in_srgb,var(--color-amber)_15%,transparent)] text-amber" : "bg-accent-dim text-accent",
                )}
              >
                <button type="button" className="px-2 py-1" onClick={() => setScheduleOpen(true)}>
                  {formatLong(scheduledDate)}
                </button>
                <button
                  type="button"
                  aria-label="Termin entfernen, sofort senden"
                  title="Termin entfernen, sofort senden"
                  className="border-l border-[color-mix(in_srgb,currentColor_30%,transparent)] px-1.5 py-1"
                  onClick={() => setScheduledAt("")}
                >
                  ×
                </button>
              </span>
            ) : (
              <Button size="sm" onClick={() => setScheduleOpen(true)}>
                Versand planen…
              </Button>
            )}
            {startInPast ? (
              <span className="text-[11px] text-amber">liegt in der Vergangenheit — geht sofort raus</span>
            ) : null}
            <div className="ml-auto flex items-center gap-1.5">
              <div className="relative">
                <Button size="sm" onClick={() => setTestOpen((open) => !open)} aria-expanded={testOpen}>
                  Testmail
                </Button>
                {testOpen ? (
                  <div className="absolute bottom-full right-0 z-30 mb-2 w-60 rounded-md border border-hairline bg-surface p-2.5 shadow-xl">
                    <label className="field-label" htmlFor="send-address">
                      Testmail an eine Adresse
                    </label>
                    <input
                      id="send-address"
                      type="email"
                      className="field mb-2"
                      placeholder="ops@example.test"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      className="w-full"
                      disabled={sending || !address.trim() || placeholderGaps.length > 0}
                      title={sendBlockedHint}
                      onClick={() => {
                        setTestOpen(false);
                        setPendingSend("one");
                      }}
                    >
                      Testmail senden
                    </Button>
                  </div>
                ) : null}
              </div>
              <Button
                variant="primary"
                size="sm"
                disabled={sending || Boolean(audienceError) || recipientCount < 1 || placeholderGaps.length > 0}
                title={sendBlockedHint}
                onClick={() => setPendingSend("filter")}
              >
                {sending ? "Senden…" : scheduledAt ? `Planen (${recipientCount})` : `Sofort senden (${recipientCount})`}
              </Button>
            </div>

            {audienceOpen ? (
              <div
                ref={audiencePopoverRef}
                className="absolute bottom-full left-4 z-30 mb-2 w-[24rem] rounded-md border border-hairline bg-surface p-3 shadow-xl"
              >
                <div className="mb-2 flex items-baseline justify-between gap-2">
                  <span className="field-label mb-0">
                    Zielgruppe{activeSource ? ` · ${activeSource.title}` : ""}
                  </span>
                  <button
                    type="button"
                    className="text-[11px] text-accent underline underline-offset-2"
                    disabled={Boolean(event.audience_snapshot_id)}
                    onClick={() => {
                      const next = !allOccupied;
                      setAllOccupied(next);
                      if (next) {
                        setSelectedShorts([]);
                        setWohnheimQuery("");
                      }
                    }}
                  >
                    {allOccupied ? "bestimmte Wohnheime wählen" : "alle Nutzer wählen"}
                  </button>
                </div>
                {enabledSources.length > 1 ? (
                  <select
                    id="import-source"
                    className="field mb-2"
                    aria-label="Quelle"
                    disabled={Boolean(event.audience_snapshot_id)}
                    value={activeSource?.slug ?? ""}
                    onChange={(e) => {
                      setSourceSlug(e.target.value);
                      setSelectedShorts([]);
                      setAllOccupied(true);
                    }}
                  >
                    {enabledSources.map((item) => (
                      <option key={item.id} value={item.slug}>
                        {item.title}
                      </option>
                    ))}
                  </select>
                ) : null}

                {allOccupied ? (
                  <div className="rounded-md border border-hairline bg-surface-subtle px-2 py-1.5 text-sm">
                    Alle Nutzer
                    {audiencePreview ? (
                      <span className="font-normal text-muted"> ({audiencePreview.recipient_count})</span>
                    ) : null}
                  </div>
                ) : (
                  <div className="rounded-md border border-hairline bg-surface-subtle">
                    <input
                      className="w-full border-b border-hairline bg-transparent px-2.5 py-1.5 text-sm outline-none placeholder:text-muted"
                      placeholder={sourceRules.length === 0 ? "Keine Wohnheime konfiguriert" : "Liste filtern…"}
                      aria-label="Wohnheime filtern"
                      disabled={sourceRules.length === 0}
                      value={wohnheimQuery}
                      onChange={(e) => setWohnheimQuery(e.target.value)}
                    />
                    <div className="max-h-56 overflow-auto py-1">
                      {visibleRules.length === 0 && sourceRules.length > 0 ? (
                        <p className="px-2.5 py-1.5 text-[11px] text-muted">Keine Treffer.</p>
                      ) : (
                        visibleRules.map((item) => (
                          <label
                            key={item.slug}
                            className="flex cursor-pointer items-center gap-2 px-2.5 py-1 text-sm hover:bg-surface"
                          >
                            <input
                              type="checkbox"
                              className="accent-[var(--color-accent)]"
                              disabled={Boolean(event.audience_snapshot_id)}
                              checked={selectedShorts.includes(item.slug)}
                              onChange={(e) => {
                                const { checked } = e.target;
                                setSelectedShorts((prev) =>
                                  checked ? [...prev, item.slug] : prev.filter((short) => short !== item.slug),
                                );
                              }}
                            />
                            {item.title || item.slug}
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {audienceError ? (
                  <p className="mt-2 text-[11px] text-danger">{audienceError}</p>
                ) : audiencePreview ? (
                  <p className="mt-2 text-[11px] text-muted">
                    {!allOccupied ? `${selectedShorts.length} von ${sourceRules.length} Wohnheimen · ` : ""}
                    {audiencePreview.recipient_count} Empfänger
                    {audiencePreview.without_address
                      ? `, ${audiencePreview.without_address} ohne Adresse`
                      : ""}
                    {audiencePreview.duplicate_users
                      ? `, ${audiencePreview.duplicate_users} Dublette(n)`
                      : ""}
                    {audiencePreview.bottleneck
                      ? ` · Engpass ${audiencePreview.bottleneck} (${audiencePreview.bottleneck_n})`
                      : ""}
                    {audiencePreview.source && audiencePreview.source !== "studnet"
                      ? ` · ${audiencePreview.source}`
                      : ""}
                    {audiencePreview.age_seconds != null
                      ? ` · Stand ${formatRelativeAge(audiencePreview.age_seconds)}${
                          audiencePreview.replica_count
                            ? ` · ${audiencePreview.replica_count} in der Quelle`
                            : ""
                        }`
                      : ""}
                    {audiencePreview.invalid_count
                      ? ` · ${audiencePreview.invalid_count} Adresse(n) mit Rückläufer-Vermerk`
                      : ""}
                    {audiencePreview.stale
                      ? " — Versand nutzt den letzten Sync, nicht live die Quelldatenbank."
                      : ""}
                    {audiencePreview.filters_drift
                      ? " · Quellfilter geändert seit dem letzten Sync — unter Quellen synchronisieren."
                      : ""}
                  </p>
                ) : (
                  <p className="mt-2 text-[11px] text-muted">Vorschau wird geladen…</p>
                )}
                {audiencePreview?.invalid_addresses?.length ? (
                  <details className="mt-1 text-[11px] text-muted">
                    <summary className="cursor-pointer">
                      Adressen mit Rückläufer-Vermerk (bleiben im Empfängerkreis)
                    </summary>
                    <ul className="mt-1 max-h-40 overflow-auto font-mono">
                      {audiencePreview.invalid_addresses.map((row) => (
                        <li key={row.address}>
                          {row.address}
                          {row.last_category ? ` · ${bounceCategoryLabel(row.last_category)}` : ""}
                          {row.last_bounce_at ? ` · ${formatDateTime(row.last_bounce_at)}` : ""}
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>

      {scheduleOpen ? (
        <ScheduleDialog
          value={scheduledAt}
          start={maintenanceBegin}
          onApply={(value) => {
            setScheduledAt(value);
            setScheduleOpen(false);
          }}
          onClose={() => setScheduleOpen(false)}
        />
      ) : null}

      {pendingSend
        ? (() => {
            const info = sendTargetInfo(pendingSend);
            const willSchedule = pendingSend === "filter" && Boolean(scheduledAt) && !locked;
            const checks = [
              {
                ok: subjectGaps.length === 0,
                label: "Betreff ohne offene Platzhalter",
                hint: subjectGaps.length
                  ? subjectGaps.map((name) => `{{${name}}}`).join(", ")
                  : "geprüft",
              },
              {
                ok: placeholderGaps.length === 0,
                label: "Alle benutzten Felder gefüllt",
                hint: placeholderGaps.length
                  ? `offen: ${gapLabels.join(", ")}`
                  : `${usedFields.length} von ${usedFields.length}`,
              },
              {
                ok: !emptyHtml(htmlEn),
                label: "Englische Fassung vorhanden",
                hint: emptyHtml(htmlEn) ? "fehlt — geht einsprachig raus" : "geprüft",
              },
            ];
            const hints = [
              ...(usedPlaceholderKeys.has("date") ? dateHints : []),
              ...(willSchedule && startInPast ? ["Start liegt in der Vergangenheit — die Mail geht sofort raus."] : []),
            ];
            const enMissing = event?.type === "maintenance" && emptyHtml(htmlEn);
            const gaps = !checks[0].ok || !checks[1].ok;
            const needsAck = enMissing && !enMissingAck;
            const blocked = gaps || needsAck;
            return (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
                <div className="w-full max-w-sm rounded-lg border border-hairline bg-surface shadow-xl">
                  <div className="px-5 pt-4 pb-3">
                    <h2 className="text-sm font-semibold">
                      {willSchedule ? "Nachricht jetzt planen?" : "Nachricht jetzt senden?"}
                    </h2>
                  </div>
                  <div className="space-y-2 px-5 pb-4">
                    <div className="flex items-start justify-between gap-3 rounded-md border border-hairline bg-bg px-3 py-2">
                      <div className="min-w-0">
                        <div className="mb-0.5 text-[10px] uppercase tracking-wide text-muted">Betreff</div>
                        <div className="truncate text-sm font-medium">
                          {subjectDe || subjectEn || "(kein Betreff)"}
                        </div>
                      </div>
                      <Badge className="shrink-0">{MESSAGE_PHASE_LABEL[phase]}</Badge>
                    </div>
                    <div className="rounded-md border border-hairline bg-bg px-3 py-2">
                      <div className="mb-0.5 text-[10px] uppercase tracking-wide text-muted">Empfänger</div>
                      <div className="flex items-baseline gap-1.5 text-sm">
                        <span>{info.label}</span>
                        {info.count != null ? (
                          <span className="font-mono font-semibold text-accent">{info.count}</span>
                        ) : null}
                      </div>
                    </div>
                    <ul className="space-y-1">
                      {checks.map((check) => (
                        <li key={check.label} className="flex items-baseline gap-2 text-xs">
                          <span className={cn("font-mono", check.ok ? "text-green" : "text-danger")}>
                            {check.ok ? "✓" : "!"}
                          </span>
                          <span className="text-ink">
                            {check.label} <span className="text-muted">— {check.hint}</span>
                          </span>
                        </li>
                      ))}
                      {hints.map((hint) => (
                        <li key={hint} className="flex items-baseline gap-2 text-xs">
                          <span className="font-mono text-amber">!</span>
                          <span className="text-ink">{hint}</span>
                        </li>
                      ))}
                    </ul>
                    {enMissing ? (
                      <div
                        role="alert"
                        className="rounded-md border border-amber/40 bg-amber/10 px-3 py-2 text-xs"
                      >
                        <p className="font-medium text-amber">Keine englische Übersetzung</p>
                        <p className="mt-0.5 text-ink">
                          Diese Wartungsmail geht nur auf Deutsch raus. Englischsprachige Bewohner
                          erhalten keine verständliche Ankündigung.
                        </p>
                        <label className="mt-2 flex cursor-pointer items-start gap-2 text-ink">
                          <input
                            type="checkbox"
                            className="mt-0.5 accent-[var(--color-accent)]"
                            checked={enMissingAck}
                            onChange={(e) => setEnMissingAck(e.target.checked)}
                          />
                          <span>Ich sende bewusst ohne englische Übersetzung.</span>
                        </label>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex items-center justify-end gap-2 border-t border-hairline bg-surface-subtle px-5 py-3">
                    {gaps ? (
                      <span className="mr-auto text-[11px] text-danger">Senden gesperrt</span>
                    ) : needsAck ? (
                      <span className="mr-auto text-[11px] text-amber">Bitte den Hinweis bestätigen</span>
                    ) : null}
                    <Button onClick={() => setPendingSend(null)}>Abbrechen</Button>
                    <Button
                      variant="primary"
                      disabled={blocked}
                      onClick={() => {
                        const confirmedTarget = pendingSend;
                        setPendingSend(null);
                        void handleSend(confirmedTarget);
                      }}
                    >
                      {willSchedule ? "Jetzt planen" : "Jetzt senden"}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })()
        : null}
    </div>
  );
}
